import React, { useState } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Divider, Paper, Grid, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Alert, Chip, Card, CardContent, Container } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import AddBoxIcon from '@mui/icons-material/AddBox';
import LinkIcon from '@mui/icons-material/Link';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GitHubIcon from '@mui/icons-material/GitHub';
import { FaBitbucket, FaGitlab } from 'react-icons/fa';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import KeyIcon from '@mui/icons-material/VpnKey';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  SetupSidebar,
  LoginStep,
  ProjectCreationStep,
  WebhookIntegrationStep,
  StartReviewingStep
} from '../components/setup';
import { FolderOpen, Key, Rocket, Webhook } from 'lucide-react';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/landing/Footer';

const SETUP_FAQS = [
  {
    question: "How long does it take to set up Sniffr.ai?",
    answer: "Most teams complete the full setup in under 10 minutes. You'll need to: (1) create an account and project, (2) configure your Git repository (GitHub, GitLab, Bitbucket, or Azure DevOps) with a webhook, and (3) connect your preferred AI analyzer. Once the webhook is active, Sniffr.ai automatically analyzes every new pull request.",
  },
  {
    question: "Do I need to modify my existing CI/CD pipeline?",
    answer: "No. Sniffr.ai uses webhooks to receive PR events directly from your Git platform. There is no need to modify your existing CI/CD pipeline, add build steps, or install agents. It works entirely independently and complements your existing workflow.",
  },
  {
    question: "What permissions does Sniffr.ai need on my Git repository?",
    answer: "Sniffr.ai requires read access to your repository to analyze pull request diffs and post review comments. For GitHub, this means a webhook configured with pull_request events. For GitLab, Azure DevOps, and Bitbucket, similar webhook permissions apply. We follow the principle of least privilege and never require write access to your codebase.",
  },
  {
    question: "Can I connect multiple repositories to one Sniffr.ai project?",
    answer: "Yes. Each Sniffr.ai project can be configured to work with multiple repositories across different Git platforms. This is ideal for microservices architectures where a single feature spans multiple repositories or services.",
  },
  {
    question: "Which AI models does Sniffr.ai support?",
    answer: "Sniffr.ai supports multiple AI backends including OpenAI GPT-4, Google Gemini, and Anthropic Claude. You can select your preferred model at the project level or switch between models based on the type of analysis needed. Enterprise plans support custom model configurations.",
  },
  {
    question: "What if my team uses JIRA for requirements management?",
    answer: "Sniffr.ai has native JIRA integration. During setup, you can connect your JIRA workspace and map JIRA projects to your Sniffr.ai projects. When a pull request references a JIRA ticket, Sniffr.ai automatically pulls the requirements and acceptance criteria and validates the code against them.",
  },
];

const ProjectCreationContent = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Step 2: Create a Project</Typography>
      <Accordion defaultExpanded sx={{ mb: 2, bgcolor: '#f8fafc' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Add Project Details & Emails</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" gutterBottom>
            <b>Project Name:</b> Used to identify your project in the dashboard.<br />
            <b>User Emails:</b> Add team members who should have access. This is required for user access control and notifications.
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            Only users with listed emails can access and manage this project.
          </Alert>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded sx={{ mb: 2, bgcolor: '#f8fafc' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Repository Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab icon={<GitHubIcon />} label="GitHub" />
            <Tab icon={<FaGitlab />} label="GitLab" />
            <Tab icon={<FaBitbucket />} label="Bitbucket" />
            <Tab icon={<IntegrationInstructionsIcon />} label="Azure DevOps" />
            <Tab icon={<IntegrationInstructionsIcon />} label="AI Analyzers" />
            <Tab icon={<KeyIcon />} label="JIRA" />
            <Tab icon={<IntegrationInstructionsIcon />} label="Trello" />
            <Tab icon={<IntegrationInstructionsIcon />} label="Zoho Sprints" />
          </Tabs>
          {tab === 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get a GitHub Token:</Typography>
              <ol>
                <li>Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">GitHub Developer Settings</a>.</li>
                <li>Click "Generate new token (classic)".</li>
                <li>Select scopes: <b>repo (full)</b>, <b>user (full)</b>, <b>admin:repo_hook</b>.</li>
                <li>Copy and paste the generated token into the GitHub Token field.</li>
              </ol>
              <Alert severity="warning">Note: These scopes are mandatory for proper functionality.</Alert>
              <Alert severity="info">Never share your token. Store it securely.</Alert>
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get a GitLab Personal Access Token:</Typography>
              <ol>
                <li>Go to <a href="https://gitlab.com/-/profile/personal_access_tokens" target="_blank" rel="noopener noreferrer">GitLab Personal Access Tokens</a>.</li>
                <li>Click "Create personal access token".</li>
                <li>Select scopes: <b>api</b> and <b>write_repository</b>.</li>
                <li>Copy the token and paste it into the GitLab Token field.</li>
              </ol>
              <Alert severity="warning">These scopes are required to fetch MRs and commit fixes.</Alert>
            </Box>
          )}
          {tab === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get Bitbucket Username & App Password:</Typography>
              <ol>
                <li>Go to <a href="https://bitbucket.org/account/settings/app-passwords/" target="_blank" rel="noopener noreferrer">Bitbucket App Passwords</a>.</li>
                <li>Click "Create app password".</li>
                <li>Set permissions: <b>Repository: Read & Write</b>, <b>Pull Requests: Read & Write</b>, <b>Webhooks: Read & Write</b>.</li>
                <li>Copy your Bitbucket username and the generated app password.</li>
                <li>Enter both in the setup form.</li>
              </ol>
              <Alert severity="warning">Note: These permissions are mandatory for proper functionality.</Alert>
              <Alert severity="info">App passwords are more secure than your main password. Store them safely.</Alert>
            </Box>
          )}
          {tab === 3 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get Azure DevOps Personal Access Token:</Typography>
              <ol>
                <li>Go to your Azure DevOps organization.</li>
                <li>Click on your profile picture &rarr; <b>Security</b>.</li>
                <li>Click <b>Personal access tokens</b> &rarr; <b>New Token</b>.</li>
                <li>Set <b>Name</b> to something like "AI Code Review Tool".</li>
                <li>Set <b>Organization</b> to "All accessible organizations".</li>
                <li>Set <b>Expiration</b> to your preference (recommend 1 year).</li>
                <li>Under <b>Scopes</b>, select <b>Custom defined</b> and check:</li>
                <ul>
                  <li><b>Code (Read & Write)</b> - to access repositories</li>
                  <li><b>Work Items (Read & Write)</b> - to access work items</li>
                  <li><b>Pull Request Threads (Read & Write)</b> - to post review comments</li>
                </ul>
                <li>Click <b>Create</b> and copy the generated token.</li>
                <li>Paste the token into the Azure DevOps Token field.</li>
              </ol>
              <Alert severity="warning">Note: These scopes are mandatory for proper functionality.</Alert>
              <Alert severity="info">Store your token securely. You won't be able to see it again after creation.</Alert>
            </Box>
          )}
          {tab === 4 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get AI Analyzer API Key:</Typography>
              <ol>
                <li>For <b>Gemini</b>: Go to <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a> and create an API key.</li>
                <li>For <b>OpenAI</b>: Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI API Keys</a> and create a new secret key.</li>
                <li>For <b>Claude</b>: Go to <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer">Anthropic Console</a> and create an API key.</li>
                <li>Paste the key into the Analyzer API Key field.</li>
              </ol>
              <Alert severity="info">Choose the provider that matches your project needs.</Alert>
            </Box>
          )}
          {tab === 5 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get a JIRA API Token:</Typography>
              <ol>
                <li>Go to <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer">Atlassian API Tokens</a>.</li>
                <li>Click "Create API token" and give it a label.</li>
                <li>Copy the generated token and use it in the JIRA Token field.</li>
                <li>Use your Atlassian email as the username.</li>
              </ol>
              <Alert severity="info">JIRA integration enables requirement validation and traceability.</Alert>
            </Box>
          )}
          {tab === 6 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to get Trello API Key and Token:</Typography>
              <ol>
                <li>Go to the <a href="https://trello.com/power-ups/admin" target="_blank" rel="noopener noreferrer">Trello Power-Up Admin Portal</a>.</li>
                <li>Create a new Power-Up or select an existing one.</li>
                <li>Navigate to the <b>"API key"</b> page in the Power-Up settings.</li>
                <li>Copy your <b>API Key</b> from the page (each Power-Up has a unique API key).</li>
                <li>To generate a token, visit <a href="https://trello.com/app-key" target="_blank" rel="noopener noreferrer">Trello App Key</a> and click the <b>"Token"</b> link.</li>
                <li>Authorize the application if prompted.</li>
                <li>Copy the generated <b>Token</b> (it will be displayed on the page).</li>
                <li>Paste both the API Key and Token into the Trello configuration fields.</li>
              </ol>
              <Alert severity="warning">Note: Keep your token secure. It provides full access to your Trello account. Each Power-Up can have a unique API key for better management.</Alert>
              <Alert severity="info">Trello integration enables card tracking, cycle time calculation, and requirement validation. Use Trello card IDs or URLs in your PR titles to link them (e.g., [TRELLO-abc12345] or https://trello.com/c/abc12345).</Alert>
            </Box>
          )}
          {tab === 7 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>How to set up Zoho Sprints Integration:</Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Step 1: Create a Self Client</Typography>
              <ol>
                <li>Go to <a href="https://api-console.zoho.in/" target="_blank" rel="noopener noreferrer">Zoho API Console</a> (use <code>.com</code> / <code>.eu</code> for other regions).</li>
                <li>Click <b>Add Client</b> → select <b>Self Client</b>.</li>
                <li>Note down the <b>Client ID</b> and <b>Client Secret</b> shown on the client page.</li>
              </ol>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Step 2: Generate a Grant Token</Typography>
              <ol>
                <li>In the Self Client page, click the <b>Generate Code</b> tab.</li>
                <li>Enter this scope: <code>ZohoSprints.teams.READ,ZohoSprints.projects.READ,ZohoSprints.items.READ,ZohoSprints.sprints.READ</code></li>
                <li>Set the time duration (e.g. 10 minutes).</li>
                <li>Select your <b>Zoho Sprints</b> portal when prompted.</li>
                <li>Click <b>Create</b> — copy the generated <b>Grant Token</b>.</li>
              </ol>
              <Alert severity="warning" sx={{ my: 1 }}>The grant token expires in a few minutes and can only be used once. Paste it into the project form promptly after generating it.</Alert>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Step 3: Fill in the form & fetch</Typography>
              <ol>
                <li>Paste <b>Client ID</b>, <b>Client Secret</b>, <b>Grant Token</b>, and select your <b>Domain</b> (zoho.in, zoho.com, zoho.eu).</li>
                <li>Click <b>"Fetch Teams & Projects"</b> — the system exchanges your grant token and loads your teams automatically.</li>
                <li>Select your <b>Team</b> from the dropdown — projects for that team load automatically.</li>
                <li>Select your <b>Project</b> from the dropdown.</li>
                <li>Save the project.</li>
              </ol>

              <Alert severity="info" sx={{ mt: 2 }}>
                <b>PR Title Format:</b> Use the item number in your PR title to link to Zoho Sprints work items.
                <br />Examples: <code>[ZOHO-3]</code>, <code>P1#I-3</code>, or <code>QDE-I3</code> (where 3 is the item number from your board).
              </Alert>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded sx={{ bgcolor: '#f8fafc' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Review Comment Mode</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" gutterBottom>
            <b>PR Comment (single summary):</b> Posts a single summary comment on the PR. Best for high-level feedback.<br />
            <b>Inline:</b> Posts comments directly on changed lines. Best for detailed, line-by-line review.<br />
            <b>Note:</b> Bitbucket projects only support Inline mode due to API comment size limits.
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            Choose the mode that matches your team's workflow. Inline is most actionable, PR Comment is concise and comprehensive. Bitbucket projects automatically use Inline mode.
          </Alert>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const WebhookIntegrationContent = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Step 3: Integrate Webhooks</Typography>
      <Typography variant="body1" gutterBottom>
        Connect your GitHub or Bitbucket repository by adding the provided webhook URL and API key. This enables automated PR analysis and review.
      </Typography>
      <Alert severity="info" sx={{ my: 2 }}>
        You can find your unique Webhook URL and API Key in the <b>Projects</b> section of the app, under each project card.
      </Alert>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<GitHubIcon />} label="GitHub" />
        <Tab icon={<FaGitlab />} label="GitLab" />
        <Tab icon={<FaBitbucket />} label="Bitbucket" />
        <Tab icon={<IntegrationInstructionsIcon />} label="Azure DevOps" />
      </Tabs>
      {tab === 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>How to add a GitHub Webhook:</Typography>
          <ol>
            <li>Go to your repository on GitHub.</li>
            <li>Click <b>Settings</b> &rarr; <b>Webhooks</b> &rarr; <b>Add webhook</b>.</li>
            <li>Paste the <b>Webhook URL</b> from your project card into the <b>Payload URL</b> field.</li>
            <li>Set <b>Content type</b> to <b>application/json</b>.</li>
            <li>Add your <b>API Key</b> as a header: <code>X-API-KEY: [your key]</code>.</li>
            <li>Select <b>Let me select individual events</b> and check <b>Pull requests</b>.</li>
            <li>Click <b>Add webhook</b> to save.</li>
          </ol>
          <Alert severity="info">Only pull request events are needed. Keep your API key secret.</Alert>
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>How to add a GitLab Webhook:</Typography>
          <ol>
            <li>Go to your project in GitLab.</li>
            <li>Click <b>Settings</b> &rarr; <b>Webhooks</b>.</li>
            <li>Paste the <b>Webhook URL</b> from your project card into the <b>URL</b> field.</li>
            <li>Add your <b>API Key</b> as a header: <code>X-API-KEY: [your key]</code>.</li>
            <li>Select trigger: <b>Merge request events</b>.</li>
            <li>Click <b>Add webhook</b> to save.</li>
          </ol>
          <Alert severity="info">Only merge request events are needed. Keep your API key secret.</Alert>
        </Box>
      )}
      {tab === 2 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>How to add a Bitbucket Webhook:</Typography>
          <ol>
            <li>Go to your repository on Bitbucket.</li>
            <li>Click <b>Repository settings</b> &rarr; <b>Webhooks</b> &rarr; <b>Add webhook</b>.</li>
            <li>Paste the <b>Webhook URL</b> from your project card into the <b>URL</b> field.</li>
            <li>Set <b>Status</b> to <b>Active</b>.</li>
            <li>Under <b>Triggers</b>, select <b>Pull request: created</b> and <b>Pull request: updated</b>.</li>
            <li>Add your <b>API Key</b> as a header: <code>X-API-KEY: [your key]</code>.</li>
            <li>Click <b>Save</b> to add the webhook.</li>
          </ol>
          <Alert severity="info">Only pull request events are needed. Keep your API key secret.</Alert>
        </Box>
      )}
      {tab === 3 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>How to add an Azure DevOps Webhook:</Typography>
          <ol>
            <li>Go to your Azure DevOps project.</li>
            <li>Click <b>Project Settings</b> &rarr; <b>Service connections</b> &rarr; <b>Webhooks</b>.</li>
            <li>Click <b>New webhook</b>.</li>
            <li>Set <b>Name</b> to something like "AI Code Review".</li>
            <li>Paste the <b>Webhook URL</b> from your project card into the <b>URL</b> field.</li>
            <li>Set <b>Status</b> to <b>Active</b>.</li>
            <li>Under <b>Triggers</b>, select <b>Pull request created</b> and <b>Pull request updated</b>.</li>
            <li>Add your <b>API Key</b> as a header: <code>X-API-KEY: [your key]</code>.</li>
            <li>Click <b>Save</b> to add the webhook.</li>
          </ol>
          <Alert severity="info">Only pull request events are needed. Keep your API key secret.</Alert>
        </Box>
      )}
    </Box>
  );
};

const setupFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": SETUP_FAQS.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
};

const SetupFAQContent = () => (
  <Box>
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(setupFaqSchema)}
      </script>
    </Helmet>
    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: '2.25rem', lineHeight: '2.5rem' }}>
      Frequently Asked Questions
    </Typography>
    <Typography variant="body1" color="#6b7280" gutterBottom sx={{ mb: 6, fontSize: '1.25rem', lineHeight: '1.75rem' }}>
      Common questions about setting up and using Sniffr.ai.
    </Typography>
    {SETUP_FAQS.map((faq, idx) => (
      <Accordion key={idx} sx={{ mb: 1.5, bgcolor: '#f8fafc', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{faq.question}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {faq.answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </Box>
);

const steps = [
  {
    label: 'Login',
    icon: <Key />,
    image: '/assets/setup-login.png',
    description: <LoginStep />,
  },
  {
    label: 'Project Creation',
    icon: <FolderOpen />,
    image: '/assets/setup-project.png',
    description: <ProjectCreationStep />,
  },
  {
    label: 'Webhook Integration',
    icon: <Webhook />,
    image: '/assets/setup-webhook.png',
    description: <WebhookIntegrationStep />,
  },
  {
    label: 'Getting Started',
    icon: <Rocket />,
    image: '/assets/setup-getting-started.png',
    description: <StartReviewingStep />,
  },
  {
    label: 'FAQs',
    icon: <HelpOutlineIcon />,
    description: <SetupFAQContent />,
  },
];

export default function Setup() {
  const [selectedStep, setSelectedStep] = useState(0);

  return (
    <>
      <SEO
        title="Setup Sniffr.ai | Connect Your Repo & Start AI Code Reviews"
        description="Get started with Sniffr.ai in minutes. Connect GitHub, GitLab, Bitbucket, or Azure DevOps and enable AI-powered code reviews and requirement validation instantly."
        keywords="Sniffr.ai setup, AI code review setup, GitHub integration, GitLab integration, webhook setup, UAT automation setup"
        url="/setup"
      />
    <Box sx={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{ 
        width: { xs: '100%', md: '20rem' },
        flexShrink: 0,
        borderRight: { md: '1px solid #e2e8f0' },
        borderBottom: { xs: '1px solid #e2e8f0', md: 'none' }
      }}>
        <SetupSidebar
          selectedStep={selectedStep}
          onStepChange={setSelectedStep}
        />
      </Box>
      <Box sx={{ 
        flex: 1,
        display: 'flex', 
        alignItems: 'flex-start',
        justifyContent: 'center', 
        p: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
        minWidth: 0
      }}>
          <Container sx={{ 
            maxWidth: { xs: '100%', sm: '100%', md: '56rem' },
            width: '100%',
            px: { xs: 2, sm: 3, md: 4 }
          }}>
            {steps[selectedStep].description}
          </Container>
      </Box>
    </Box>
    </>
  );
} 